import { useState, useEffect } from 'react';
import { supabase } from '@/integrations/supabase/client';

interface SiteContent {
  content_key: string;
  content_type: string;
  title?: string;
  content?: string;
  image_path?: string;
  alt_text?: string;
  link_url?: string;
  display_order: number;
  is_active: boolean;
}

export const useSiteContent = (contentKey?: string) => {
  const [content, setContent] = useState<SiteContent[]>([]);
  const [loading, setLoading] = useState(true);

  const getImageUrl = (path?: string) => {
    if (!path) return null;
    const { data } = supabase.storage.from('site-assets').getPublicUrl(path);
    return data.publicUrl;
  };

  const getContentByKey = (key: string): SiteContent | null => {
    return content.find(item => item.content_key === key) || null;
  };

  const getContentText = (key: string, fallback: string = ''): string => {
    const contentItem = getContentByKey(key);
    return contentItem?.content || fallback;
  };

  const getContentImage = (key: string): { url: string | null; alt: string } => {
    const contentItem = getContentByKey(key);
    return {
      url: contentItem?.image_path ? getImageUrl(contentItem.image_path) : null,
      alt: contentItem?.alt_text || ''
    };
  };

  useEffect(() => {
    const fetchContent = async () => {
      setLoading(true);
      
      let query = supabase
        .from('site_content')
        .select('*')
        .eq('is_active', true)
        .order('display_order');

      if (contentKey) {
        query = query.eq('content_key', contentKey);
      }

      const { data, error } = await query;

      if (error) {
        console.error('Error fetching site content:', error);
      } else {
        setContent(data || []);
      }
      
      setLoading(false);
    };

    fetchContent();

    // Listen for content updates
    const handleContentUpdate = () => {
      fetchContent();
    };

    window.addEventListener('site-content-updated', handleContentUpdate);

    return () => {
      window.removeEventListener('site-content-updated', handleContentUpdate);
    };
  }, [contentKey]);

  return {
    content,
    loading,
    getContentByKey,
    getContentText,
    getContentImage,
    getImageUrl
  };
};